import { useState, type FormEvent } from "react";
import { useNotify, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { QuoterScheme } from "../types";
import { useSaveQuoterConfig } from "./quoterAdminData";
import {
  EMPTY_SCHEME_FORM,
  SCHEME_FIELDS,
  schemeToFormValues,
  toSchemeData,
  validateSchemeForm,
  type SchemeFormValues,
} from "./quoterConfigRules";
import type { QuoterProjectData } from "./quoterEngine";
import { QuoterField } from "./QuoterField";

const SchemeDialog = ({
  scheme,
  onClose,
  onSave,
}: {
  scheme: QuoterScheme | null;
  onClose: () => void;
  onSave: (data: ReturnType<typeof toSchemeData>) => Promise<void>;
}) => {
  const translate = useTranslate();
  const [values, setValues] = useState<SchemeFormValues>(
    scheme ? schemeToFormValues(scheme) : EMPTY_SCHEME_FORM,
  );
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const errors = validateSchemeForm(values);
  const hasErrors = Object.keys(errors).length > 0;
  // The "do not sum 100" message is live; the rest show after a first submit.
  const err = (field: keyof typeof errors) =>
    errors[field] && (submitted || field === "sum") ? errors[field] : null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (hasErrors) return;
    setSaving(true);
    try {
      await onSave(toSchemeData(values));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {translate(
              scheme
                ? "crm.quoter.schemes.edit_title"
                : "crm.quoter.schemes.new_title",
            )}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          {SCHEME_FIELDS.map((field) => (
            <QuoterField
              key={field}
              id={`quoter-scheme-${field}`}
              label={translate(`crm.quoter.schemes.fields.${field}`)}
              value={values[field]}
              numeric={field !== "name"}
              error={err(field)}
              onChange={(value) =>
                setValues((current) => ({ ...current, [field]: value }))
              }
            />
          ))}
          {err("sum") ? (
            <p role="alert" className="text-base font-medium text-destructive">
              {translate("crm.quoter.config.errors.sum_not_100")}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={onClose}
            >
              {translate("crm.quoter.config.cancel")}
            </Button>
            <Button
              type="submit"
              className="h-11 sm:h-9"
              disabled={saving || errors.sum != null}
            >
              {translate("crm.quoter.config.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Almikia Epic EQ2 (TASK-099): payment schemes of a project (% down payment /
 * monthly / deed + discount). The three percentages must sum 100; an invalid
 * scheme is rejected inline with a plain Spanish message and never reaches the
 * provider. A scheme is archived, never deleted.
 */
export const QuoterSchemesEditor = ({
  project,
}: {
  project: QuoterProjectData;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const save = useSaveQuoterConfig();
  // undefined = closed, null = creating, a scheme = editing it.
  const [editing, setEditing] = useState<QuoterScheme | null | undefined>(
    undefined,
  );
  const schemes = [...project.schemes].sort(
    (a, b) => a.sort_order - b.sort_order,
  );

  const persist = async (
    scheme: QuoterScheme | null,
    data: Record<string, unknown>,
  ) => {
    try {
      await save(
        "quoter_schemes",
        scheme?.id ?? null,
        scheme
          ? data
          : {
              ...data,
              project_id: project.config.project_id,
              is_custom_allowed: false,
              sort_order:
                Math.max(-1, ...schemes.map((row) => row.sort_order)) + 1,
            },
      );
      notify("crm.quoter.config.saved");
      setEditing(undefined);
    } catch {
      notify("crm.quoter.config.save_error", { type: "error" });
    }
  };

  return (
    <section
      className="space-y-3"
      aria-label={translate("crm.quoter.schemes.title")}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          {translate("crm.quoter.schemes.title")}
        </h2>
        <Button
          type="button"
          variant="outline"
          className="h-11 sm:h-9"
          onClick={() => setEditing(null)}
        >
          {translate("crm.quoter.schemes.new")}
        </Button>
      </div>
      <ul className="space-y-2" data-testid="scheme-list">
        {schemes.map((scheme) => (
          <li key={scheme.id}>
            <Card>
              <CardContent className="space-y-2 p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium">{scheme.name}</span>
                  {scheme.archived_at ? (
                    <span className="text-sm text-muted-foreground">
                      {translate("crm.quoter.config.archived")}
                    </span>
                  ) : null}
                </div>
                <p className="text-base text-muted-foreground tabular-nums sm:text-sm">
                  {translate("crm.quoter.schemes.summary", {
                    down: scheme.down_pct,
                    monthly: scheme.monthly_pct,
                    deed: scheme.deed_pct,
                    discount: scheme.discount_pct,
                  })}
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-11 sm:h-8"
                    onClick={() => setEditing(scheme)}
                  >
                    {translate("crm.quoter.config.edit")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-11 sm:h-8"
                    onClick={() =>
                      persist(scheme, {
                        archived_at: scheme.archived_at
                          ? null
                          : new Date().toISOString(),
                      })
                    }
                  >
                    {translate(
                      scheme.archived_at
                        ? "crm.quoter.config.reactivate"
                        : "crm.quoter.config.archive",
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
      {editing !== undefined ? (
        <SchemeDialog
          scheme={editing}
          onClose={() => setEditing(undefined)}
          onSave={(data) => persist(editing, data)}
        />
      ) : null}
    </section>
  );
};
